import { ReservationQuoteStatuses, hydrateReservationQuoteStatus } from './ReservationQuoteStatuses/ReservationQuoteStatus';
import { modelState } from '@/utils/modelState';
import { ReservationState } from './types';
import { isReservationQuoteStatus } from './ReservationStatus';
import { addDays } from 'date-fns';

export interface ReservationQuoteDto {
    expiresAt: string;
    status: ReservationQuoteStatuses.ReservationQuoteStatusDto;
    tempReservationEnabled: boolean;
    tempReservationReleaseAfter?: string | null;
}

export interface ReservationQuote {
    ExpiresAt: modelState<Date>;
    Status: modelState<ReservationQuoteStatuses.ReservationQuoteStatus>;
    StatusIsDirty: modelState<boolean>;
    StatusVersion: modelState<number>;
    TempReservationEnabled: modelState<boolean>;
    TempReservationReleaseAfter: modelState<Date | null>;
}

export function hydrateReservationQuote(reservation: ReservationState) {
    if (isReservationQuoteStatus(reservation.ReservationStatus.value) &&
        !reservation.Quote.value) {
        reservation.Quote.setValue({
            ExpiresAt: modelState(addDays(new Date(), 3)),
            Status: modelState(ReservationQuoteStatuses.createReservationQuoteInitialEnquiryStatus()),
            StatusIsDirty: modelState(false),
            StatusVersion: modelState(0),
            TempReservationEnabled: modelState(false),
            TempReservationReleaseAfter: modelState(null),
        });
    }

    const isDirty = () => {
        if (reservation.Quote.isDirty) {
            return true;
        }

        if (reservation.Quote.value) {
            return reservation.Quote.value.ExpiresAt.isDirty ||
                reservation.Quote.value.StatusIsDirty.value ||
                reservation.Quote.value.TempReservationEnabled.isDirty ||
                reservation.Quote.value.TempReservationReleaseAfter.isDirty;
        }

        return false;
    }

    const signal = () => {
        reservation.QuoteIsDirty.setValue(isDirty());
        reservation.QuoteVersion.setValue(reservation.QuoteVersion.value + 1);
    };

    // Amount strategy
    let oldQuoteSubscription: () => void = null;

    function subscribeQuote() {
        if (reservation.Quote.value) {
            const quote = reservation.Quote.value;

            const subscriptions = [
                quote.ExpiresAt.subscribe(() => {
                    signal();
                }),
                quote.StatusIsDirty.subscribe(() => {
                    signal();
                }),
                quote.TempReservationEnabled.subscribe(() => {
                    signal();
                }),
                quote.TempReservationReleaseAfter.subscribe(() => {
                    signal();
                })
            ];

            hydrateReservationQuoteStatus(quote);

            oldQuoteSubscription = () => {
                subscriptions.forEach(sub => sub());
            };
        }
    }

    subscribeQuote();

    const unsubscribeReservationStatus = reservation.ReservationStatus.subscribe(() => {
        oldQuoteSubscription?.();

        subscribeQuote();

        signal();
    });

    return unsubscribeReservationStatus;
}
